import { useState } from "react";
import { Radio, Loader2, Copy, ExternalLink, Languages } from "lucide-react";
import { toast } from "sonner";
import { liveAssist } from "../lib/api";
import PostKit from "../components/PostKit";
import { T } from "@/locales/tr";

const LANGS = [
  { v: "fr", l: T("Français") },
  { v: "wo", l: "Wolof" },
  { v: "ar", l: "العربية" },
  { v: "en", l: "English" },
  { v: "es", l: "Español" },
  { v: "pt", l: T("Português") },
];

export default function Live() {
  const [question, setQuestion] = useState("");
  const [topic, setTopic] = useState("");
  const [lang, setLang] = useState("fr");
  const [result, setResult] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!question.trim()) { toast.error(T("Colle la question ou le commentaire du live.")); return; }
    setLoading(true); setResult("");
    try {
      const data = await liveAssist({ question, topic, target_language: lang });
      setResult(data.text);
      toast.success(T("3 réponses prêtes à coller."));
    } catch { toast.error(T("Le copilote est temporairement silencieux.")); }
    finally { setLoading(false); }
  };

  return (
    <div data-testid="page-live" className="max-w-6xl mx-auto px-4 sm:px-8 py-10">
      <div className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--papicoo-accent)]">{T("Copilote Live — TikTok")}</div>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[color:var(--papicoo-ink)] mt-2 leading-[0.98]">
        {T("Régulateur des lives de")}<br />
        <span className="italic font-light">{T("Cheikh Mbacké Sakho.")}</span>
      </h1>
      <p className="text-[color:var(--papicoo-muted)] mt-4 max-w-2xl leading-relaxed">
        {T("Colle une question entendue dans le live. Papicoo te livre")} <strong>{T("3 réponses ≤ 300 caractères")}</strong> {T("(apaisante, ferme & juste, élève les consciences) — chacune avec sa référence coranique. Prêtes à coller dans le chat TikTok.")}
      </p>

      <div className="mt-4 flex flex-wrap gap-2 text-xs">
        <a href="https://www.tiktok.com/@www.papicoo.fr" target="_blank" rel="noopener noreferrer" data-testid="link-tiktok" className="inline-flex items-center gap-2 papicoo-link-underline text-[color:var(--papicoo-ink)]">
          <Radio size={14} /> {T("TikTok @www.papicoo.fr")}
          <ExternalLink size={12} />
        </a>
        <a href="https://quranic-overlay-hub.emergent.host/" target="_blank" rel="noopener noreferrer" data-testid="link-library" className="inline-flex items-center gap-2 papicoo-link-underline text-[color:var(--papicoo-ink)]">
          {T("📖 Bibliothèque Coran (overlay broadcasting)")}
          <ExternalLink size={12} />
        </a>
      </div>

      <div className="mt-8 grid lg:grid-cols-2 gap-6">
        <div className="papicoo-card p-6 space-y-4">
          <div>
            <label className="text-[11px] uppercase tracking-[0.2em] text-[color:var(--papicoo-muted)]">{T("Sujet du live (optionnel)")}</label>
            <input
              data-testid="live-topic"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder={T("Ex : patience, justice, divorce, jeunesse, halâl…")}
              className="w-full mt-2 bg-[color:var(--papicoo-sand)] border border-[color:var(--papicoo-border)] rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--papicoo-ink)]/30"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-[0.2em] text-[color:var(--papicoo-muted)]">{T("Question / commentaire entendu")}</label>
            <textarea
              data-testid="live-question"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              rows={6}
              placeholder={T("Colle ici la question, le doute ou le commentaire du live.")}
              className="w-full mt-2 bg-[color:var(--papicoo-sand)] border border-[color:var(--papicoo-border)] rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--papicoo-ink)]/30"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-[0.2em] text-[color:var(--papicoo-muted)] inline-flex items-center gap-2">
              <Languages size={14} /> {T("Langue de la réponse")}
            </label>
            <div className="mt-2 flex flex-wrap gap-2">
              {LANGS.map((L) => (
                <button
                  key={L.v}
                  data-testid={`live-lang-${L.v}`}
                  onClick={() => setLang(L.v)}
                  className={`px-3 py-2 text-xs rounded-full ${lang === L.v ? "papicoo-btn-gold" : "bg-white border border-[color:var(--papicoo-border)]"}`}
                >
                  {T(L.l)}
                </button>
              ))}
            </div>
          </div>
          <button data-testid="live-go-btn" onClick={submit} disabled={loading} className="inline-flex items-center gap-2 px-5 py-3 rounded-full papicoo-btn-primary disabled:opacity-50">
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Radio size={16} />}
            {loading ? T("Préparation…") : T("Générer 3 réponses live")}
          </button>
        </div>

        <div data-testid="live-result">
          {result ? (
            <>
            <div className="papicoo-card p-6 max-h-[80vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-3">
                <div className="font-display text-2xl text-[color:var(--papicoo-ink)]">{T("3 réponses prêtes à coller")}</div>
                <button data-testid="live-copy" onClick={() => { navigator.clipboard.writeText(result); toast.success(T("Copié.")); }} className="text-xs inline-flex items-center gap-1 text-[color:var(--papicoo-muted)] hover:text-[color:var(--papicoo-ink)]">
                  <Copy size={14} /> {T("Copier")}
                </button>
              </div>
              <pre className="whitespace-pre-wrap font-sans text-[15px] leading-relaxed">{result}</pre>
            </div>
            <PostKit text={result} title={T("Joins la photo officielle Papicoo aux commentaires live")} />
            </>
          ) : (
            <div className="papicoo-card p-6 text-sm italic text-[color:var(--papicoo-muted)]">
              {T("Force tranquille — colle la question, choisis la langue, Papicoo te prépare 3 répliques calibrées pour TikTok (≤ 300 caractères).")}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
